import { addLocale, getLocale } from '@umijs/max';

export type SupportedLocale = 'en-US' | 'zh-CN' | 'zh-TW';

const DEFAULT_LOCALE: SupportedLocale = 'zh-TW';

const localeLoaders: Record<SupportedLocale, () => Promise<{ default: Record<string, string> }>> = {
  'en-US': () => import('@/localeBundles/en-US'),
  'zh-CN': () => import('@/localeBundles/zh-CN'),
  'zh-TW': () => import('@/localeBundles/zh-TW'),
};

const loadedLocales = new Set<SupportedLocale>();
const localeLoadPromises = new Map<SupportedLocale, Promise<void>>();

export const normalizeSupportedLocale = (locale?: string | null): SupportedLocale => {
  const normalized = String(locale ?? '').toLowerCase();
  if (normalized === 'en-us') return 'en-US';
  if (normalized === 'zh-cn') return 'zh-CN';
  return DEFAULT_LOCALE;
};

export const ensureLocaleLoaded = async (locale = getLocale()): Promise<void> => {
  const supportedLocale = normalizeSupportedLocale(locale);
  if (loadedLocales.has(supportedLocale)) {
    return;
  }

  const existingPromise = localeLoadPromises.get(supportedLocale);
  if (existingPromise) {
    return existingPromise;
  }

  const loadPromise = localeLoaders[supportedLocale]()
    .then(({ default: messages }) => {
      // Umi registers lightweight locale shells synchronously. Adding the selected
      // message bundle here updates the mounted Intl provider without a page reload.
      addLocale(supportedLocale, messages, {} as Parameters<typeof addLocale>[2]);
      loadedLocales.add(supportedLocale);
    })
    .finally(() => {
      localeLoadPromises.delete(supportedLocale);
    });

  localeLoadPromises.set(supportedLocale, loadPromise);
  return loadPromise;
};
